<template>
  <div class="payment-container">
    <div class="navigation-bar">
      <span @click="back" class="back-button">
        <font-awesome-icon icon="fa-solid fa-arrow-left"></font-awesome-icon>
      </span>
      <div class="d-flex flex-row">
        <div class="d-flex flex-column">
          <span class="balance-label">Balance Due:</span>
          <h3 class="balance-amount"> {{ $utils.formatMoney(saleTotal - discount.total) }}</h3>
        </div>
      </div>
    </div>

    <custom-modal :is-form="true" ref="lpoModal" @submit="setLPO" title="Select LPO Customer">
      <div class="form-group mb-3">
        <label for="lpo_debtor" class="form-label">Customer</label>
        <v-select id="lpo_debtor"
                  v-model="lpoDetails.debtorID"
                  :options="$store.getters.getDebtors"
                  :reduce="debtor=>debtor.Id"
                  label="Name"
                  :required="!lpoDetails.debtorID"
                  @search="searchDebtors"
        ></v-select>
      </div>

      <div class="form-group mb-3">
        <label for="lpo_number" class="form-label">
          LPO NO.
          <input class="form-control" required type="text" minlength="10" maxlength="10" v-model="lpoDetails.lpoNumber" id="lpo_number">
        </label>
      </div>
    </custom-modal>

    <div class="payment-body">
      <!-- Main Payment Section -->
      <div class="main-payment-section">
        <!-- Amount Input and Keypad Section -->
        <div class="amount-keypad-section">
          <div class="amount-input-container" v-if="updateCardTotal">
            <label class="amount-input-label">
              <b>Amount Paid</b>
              <input class="input-amount"
                     oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '0');"
                     pattern="[+-]?([0-9]*[.])?[0-9]+" type="text" v-model="paidAmount">
            </label>
          </div>

          <!-- Keypad -->
          <div class="keypad-container" v-if="updateCardTotal">
            <div class="keypad-grid">
              <button @click="inputNumber($event)" class="number-btn" type="button">1</button>
              <button @click="inputNumber($event)" class="number-btn" type="button">2</button>
              <button @click="inputNumber($event)" class="number-btn" type="button">3</button>
              <button @click="inputNumber($event)" class="number-btn" type="button">4</button>
              <button @click="inputNumber($event)" class="number-btn" type="button">5</button>
              <button @click="inputNumber($event)" class="number-btn" type="button">6</button>
              <button @click="inputNumber($event)" class="number-btn" type="button">7</button>
              <button @click="inputNumber($event)" class="number-btn" type="button">8</button>
              <button @click="inputNumber($event)" class="number-btn" type="button">9</button>
              <button @click="addPoint" class="number-btn function-btn" type="button">.</button>
              <button @click="inputNumber($event)" class="number-btn" type="button">0</button>
              <button @click="backspace" class="number-btn function-btn" type="button">
                <font-awesome-icon icon="fa-solid fa-delete-left"/>
              </button>
            </div>
          </div>
        </div>

        <!-- Payment Methods Section -->
        <div class="payment-methods-section">
          <DesktopPaymentMethod :minimum-amount="paidAmount" @processPayment="processTransaction" @show-room-selection="showRoomSelection" />

          <!-- Tip Badge -->
          <div class="tip-badge-container" v-if="tipAmount > 0">
            <span class="badge bg-info">Tip: {{ $utils.formatMoney(tipAmount) }}</span>
          </div>
        </div>
      </div>

      <!-- Bottom Section: Purchase Order, Discount, and Reference -->
      <div class="bottom-section">
        <!-- Purchase Order and Discount Row -->
        <div class="order-discount-row">
          <!-- LPO Section -->
          <div class="lpo-section">
            <h6>VAT Exemption LPO</h6>
            <div class="lpo-content">
              <a class="btn lpo-btn" :class="{ disabled: !isOnline }" @click="openLPO" v-if="!storeLPONumber && isOnline">Add LPO</a>
              <a class="btn lpo-btn" :class="{ disabled: !isOnline }" v-else-if="!storeLPONumber && !isOnline" style="opacity: 0.6; cursor: not-allowed;">Add LPO (Offline)</a>
              <a class="btn lpo-btn lpo-remove-btn" :class="{ disabled: !isOnline }" @click="removeLPO" v-else-if="storeLPONumber && isOnline">Remove LPO</a>
              <a class="btn lpo-btn lpo-remove-btn" :class="{ disabled: !isOnline }" v-else-if="storeLPONumber && !isOnline" style="opacity: 0.6; cursor: not-allowed;">Remove LPO (Offline)</a>
            </div>
          </div>

          <!-- Discount Section -->
          <div class="discount-section">
            <h6>Apply Discount (Optional)</h6>
            <div class="discount-box" @click="openDiscount">
              <div class="discount-content">
                <p v-if="!discountApplied">Tap to apply discount</p>
                <p v-else>{{ discountText }}</p>
              </div>
              <div class="discount-actions">
                <a v-if="discountApplied" class="icon-btn text-danger" @click.stop="clearDiscount">
                  <font-awesome-icon icon="fa-solid fa-xmark"/>
                </a>
                <a class="icon-btn">
                  <font-awesome-icon icon="fa-solid fa-chevron-right"/>
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Reference Section -->
        <div class="reference-section">
          <label for="saleReference" class="form-label">Purchase Order / Reference / Note(s)</label>
          <textarea class="form-control" placeholder="Purchase Order / Reference / Note(s)" rows="2"
                    required id="saleReference" v-model="reference"/>
        </div>
      </div>

      <!-- Mobile Process Button -->
      <div class="mobile-process-button-container" v-if="mobile">
        <button :style="disabled" @click="process" class="payment-btn lyt-soft-btn">
          <span v-if="loading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          {{ textMsg }}
        </button>
      </div>
    </div>

    <DiscountSheet ref="discount"/>
    <MethodsBottomSheet ref="paymentMethods"/>
    <RoomSelectionModal :show="showRoomModal" @close="closeRoomModal" @room-selected="handleRoomSelected"/>
  </div>
</template>

<script>
import DesktopPaymentMethod from "@/components/point-of-sale/sales/payment/DesktopPaymentMethod";
import DiscountSheet from "@/components/point-of-sale/sales/Discount/DiscountSheet";
import CurrentSaleTable from "@/components/point-of-sale/sales/CurrentSaleTable";
import ViewItems from "@/components/point-of-sale/sales/payment/ViewItems";
import SaleDetails from "@/components/point-of-sale/sales/SaleDetails";
import MethodsBottomSheet from "@/components/point-of-sale/sales/payment/MethodsBottomSheet";
import CustomModal from "@/components/layout/utils/CustomModal";
import * as responses from "@/utils/responses"; // Import responses
import Swal from 'sweetalert2'; // Import Swal if not global
import connectivityMonitor from '@/services/connectivityMonitor';
import RoomSelectionModal from "@/components/point-of-sale/sales/payment/RoomSelectionModal.vue";
import LodgingIntegration from "@/services/lodgingIntegration";
import {Storage} from '@/services/Storage';
import axios from 'axios';
import {mapActions, mapGetters} from 'vuex';
import tableServerClient from "@/services/tableServerClient";

export default {
  name: "PaymentScreen",
  components: {
    DesktopPaymentMethod,
    CustomModal,
    MethodsBottomSheet,
    SaleDetails,
    ViewItems,
    CurrentSaleTable,
    DiscountSheet,
    RoomSelectionModal,
  },
  computed: {
    ...mapGetters(['getCurrentTheme']),
    currentTheme() {
      return this.getCurrentTheme;
    },
    saleUnits() {
      return this.$store.getters.getSaleUnits;
    },
    tipAmount() {
      return this.$store.getters.getTipAmount;
    },
    discountApplied() {
      return this.discount.type !== 'none';
    },
    disabled() {
      return this.invalid || this.loading
          ? { background: `var(--disabled-button-bg)`, "pointer-events": "none", color: `var(--disabled-button-text)` }
          : { background: `linear-gradient(135deg, var(--accent-electric-blue), var(--accent-neon-purple))` };
    },
    invalid() {
      if (this.paidAmount === "") return false;
      let paid = isNaN(parseFloat(this.paidAmount)) ? 0 : parseFloat(this.paidAmount);
      let balance = parseFloat(this.saleTotal) - parseFloat(this.discount.total);
      return paid < balance;
    },
    saleTotal() {
      return this.$store.getters.getSaleTotal + this.$store.getters.getTipAmount;
    },
    discountText() {
      let discount = this.discount;
      return `${discount.type} (${discount.type === "Percentage" ? `${discount.value}%` : discount.value})`;
    },
    discount() {
      return this.$store.getters.getSaleDiscount;
    },
    change() {
      let val = this.paidAmount - Number((this.saleTotal - this.discount.total))
      return val < 0 ? 0 : val.toFixed(2);
    },
    textMsg() {
      return this.loading ? "Processing Transaction" : "Process Transaction";
    },
    saleDetails() {
      return this.$store.getters.getSaleDetails
    },
    currentSaleLines() {
      return this.$store.getters.getSaleLines
    },
    storeLPONumber() {
      return this.$store.getters.getLPO;
    },
    updateCardTotal() {
      return this.$store.dispatch(`validateSecurityPermissionNoPrompt`,`updateCardTotal`);
    },
  },
  data() {
    return {
      mobile: false,
      paidAmount: "",
      paymentType: "Cash",
      loading: false,
      reference: "",
      isOnline: true,
      unsubscribeOnline: null,
      unsubscribeOffline: null,
      showRoomModal: false,
      selectedRoom: null,
      lpoDetails: {
        debtorID: null,
        lpoNumber: null,
      },
    };
  },
  created() {
    // Initialize theme before component renders
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      document.documentElement.setAttribute('data-theme', savedTheme);
    }
    this.initializeTheme();

    // Initialize online status
    this.isOnline = connectivityMonitor.getStatus();

    // Subscribe to connectivity events
    this.unsubscribeOnline = connectivityMonitor.subscribe('online', () => {
      this.isOnline = true;
    });

    this.unsubscribeOffline = connectivityMonitor.subscribe('offline', () => {
      this.isOnline = false;
    });

    // Screen size check
    this.checkScreenSize();
    window.addEventListener('resize', this.checkScreenSize);
  },
  beforeUnmount() {
    // Cleanup
    window.removeEventListener('resize', this.checkScreenSize);
    if (this.unsubscribeOnline) {
      this.unsubscribeOnline();
    }
    if (this.unsubscribeOffline) {
      this.unsubscribeOffline();
    }
  },
  methods: {
    ...mapActions(['initializeTheme']),
    checkScreenSize() {
      this.mobile = window.innerWidth <= 768;
    },
    clearDiscount() {
      return this.$store.commit("clearDiscount");
    },


    showRoomSelection() {
      this.showRoomModal = true;
    },

    closeRoomModal() {
      this.showRoomModal = false;
    },

    async handleRoomSelected(room) {
      this.selectedRoom = room;
      this.showRoomModal = false;

      try {
        this.$utils.showLoading();
        const bookingId = room.bookingId || room.id;
        const saleLines = this.$store.getters.getSaleLines || [];
        const saleAmount = this.$store.getters.getSaleTotal;
        const saleDiscount = this.discount.total || 0;
        const tipAmount = this.$store.getters.getTipAmount || 0;
        const netAmount = saleAmount - saleDiscount;

        // For non-2FA rooms, post per-line charges to SmartLodging
        if (!room.bookingOtpTransactionId) {
          // Check service budget on the total
          const budgetCheck = await LodgingIntegration.checkServiceBudget(bookingId, netAmount);
          if (budgetCheck && budgetCheck.allowed === false) {
            const remaining = budgetCheck.remainingLimit != null
              ? this.$utils.formatMoney(budgetCheck.remainingLimit)
              : 'N/A';
            Swal.fire({
              title: 'Budget Exceeded',
              text: `This charge exceeds the guest\'s service budget. Remaining limit: ${remaining}`,
              icon: 'warning',
              confirmButtonText: 'OK'
            });
            return;
          }

          // Post one charge per sale line — each with its own SKU
          for (const line of saleLines) {
            const qty = Number(line.Qty) || 1;
            const unit = Number(line.Unit) || 0;
            const levy = Number(line.tourismLevy || 0);
            const lineGross = (qty * unit) + (qty * levy);

            // Include cooking instruction costs
            let instructionCost = 0;
            if (line.instructions && line.instructions.length > 0) {
              instructionCost = line.instructions.reduce(
                (acc, i) => acc + (Number(i.Price) * Number(i.count || 1)), 0
              );
            }

            const chargeAmount = lineGross + instructionCost;

            // Distribute sale-level discount proportionally across lines
            const lineShare = saleAmount > 0 ? chargeAmount / saleAmount : 0;
            const proportionalDiscount = Number((lineShare * saleDiscount).toFixed(2));
            const lineDiscount = Number(line.Discount || 0) + proportionalDiscount;

            await LodgingIntegration.addRoomCharge(bookingId, {
              description: line.Description,
              amount: chargeAmount,
              itemType: 'RESTAURANT',
              sku: line.Plu || null,
              department: line.Department || 'RESTAURANT',
              quantity: qty,
              discountAmount: lineDiscount,
              invoiceRef: null
            });
            console.log('[RoomSelection] Line department debug:', {
              description: line.Description,
              lineDepartment: line.Department,
              fallbackUsed: !line.Department
            });
          }
        }

        // Capture sale number - try store first, fall back to route query param
        // (route is used when navigating from AllTables to PaymentView)
        const storeDb = await Storage.getCurrentStoreDB();
        const saleNumber = this.$store.getters.getCurrentSale || this.$route.query.saleNumber;

        // No POS invoice — charge is on the lodge bill.
        // Clear the sale and navigate back.
        this.$store.commit('clearSale');
        // Explicitly clear openTableName to prevent "Open Table" alert in NewShoppingChart
        // when returning to POS after a room payment
        this.$store.commit('setOpenTableName', null);
        this.$store.commit('setOpenTableName', null, { root: true });

        // Mark table as processed on backend (OnlineSalesServer)
        axios.post(`${window.serverApi}tables/${storeDb}/markProcessed/${saleNumber}`)
          .catch(err => console.error('Failed to mark table as processed on backend:', err));

        // Also update the local table server to mark table as processed
        // This ensures non-main PCs also hide the table immediately
        if (saleNumber && saleNumber > 0) {
          try {
            await tableServerClient.updateTable(
              { saleNumber, SaleNum: saleNumber, processed: true },
              storeDb,
              false
            );
            console.log('[handleRoomSelected] Table marked as processed in local server:', saleNumber);
          } catch (err) {
            console.error('Failed to mark table as processed in local server:', err);
          }
        }

        Swal.fire({
          title: 'Charged to Room',
          text: `${this.$utils.formatMoney(netAmount)} charged to Room ${room.roomNumber} (${room.guestName})`,
          icon: 'success',
          timer: 2500,
          showConfirmButton: false
        });

        this.$router.push({ name: 'pos' });
      } catch (error) {
        console.error('Failed to process room charge:', error);
        responses.showError(error.message || 'Failed to charge to room');
      } finally {
        this.$utils.hideLoading();
      }
    },

    async openDiscount() {
      let isAllowed = await this.$store.dispatch(`validateSecurityPermission`, `Discount`)
      if (!isAllowed) return;
      if (this.$refs.discount && typeof this.$refs.discount.open === 'function') {
        this.$refs.discount.open();
      } else {
        console.error("Discount ref is undefined or does not have an 'open' method");
      }
    },
    checkPaidAmount() {
      if (this.paidAmount === "") {
        let amount = parseFloat(this.saleTotal) - parseFloat(this.discount.total);
        return amount < 0 ? 0 : amount;
      }
      return this.paidAmount;
    },
    updatePaidAmount(value) {
      this.paidAmount = parseFloat(value).toFixed(2);
    },
    backspace() {
      this.paidAmount = this.paidAmount.slice(0, -1);
    },
    addPoint() {
      if (!this.paidAmount.includes(".")) this.paidAmount += ".";
    },
    process() {
      if (this.currentSaleLines.length === 0) {
        return responses.showWarning('You need to add Items to Sale!');
      }

      this.$store.commit('setSaleDetails', {
        refund: false,
        reference: this.reference,
        total: parseFloat(this.saleTotal.toFixed(2)),
        paid: this.checkPaidAmount(),
        change: parseFloat(Number(this.change).toFixed(2))
      });

      if (this.$refs.paymentMethods && this.$refs.paymentMethods.open) {
        this.$refs.paymentMethods.open();
      } else {
        responses.showWarning("Payment methods not available."); // Added a warning for when paymentMethods is not available.
      }
    },

    async openCashDrawer() {
      let isAllowed = await this.$store.dispatch(`validateSecurityPermission`, `OpenDrwer`);
      if (isAllowed) return this.$store.dispatch(`openCashDrawer`)
    },
    back() {
      this.$store.dispatch(`calculateTipAmount`);
      if (this.discountApplied) {
        this.$store.commit("clearDiscount");
      }
      return this.$router.push({
        name: `pos`,
        query: {
          saleNumber: this.$store.getters.getCurrentSale,
          dontReload: true
        }
      });
    },
    processTransaction: function (type) {
      this.$utils.showLoading();
      // this.setSwipe();
      let obj = this.saleDetails;
      obj.type = type;
      this.openCashDrawer(); // Call openCashDrawer method here
      return this.$store.dispatch("processTransaction", {
        ...obj,
        refund: false,
        reference: this.reference,
        total: parseFloat(this.saleTotal.toFixed(2)),
        paid: this.checkPaidAmount(),
        change: parseFloat(Number(this.change).toFixed(2))
      })
          .then((res) => {
            // Let the store action handle routing based on hideNextSale setting
            // The processTransaction action will route to either 'pos' or 'viewPayment'
            // based on the hideNextSale device setting
            if (res) {
              if (!androidMode) {
              }
              // Routing is now handled by the store action
            }
          }).finally(() => {
            // this.setSwipe();
            this.$utils.hideLoading();
          });
    },
    inputNumber(event) {
      let elem = event.target
      if (elem) {
        let txt = elem.textContent || elem.innerText;
        if (txt) {
          if (this.paidAmount === '0') {
            this.paidAmount = ''
          }
          this.paidAmount += txt
        }
      }
    },
    setLPO() {
      if (!this.lpoDetails.debtorID) {
        responses.showWarning(`Please Select a Customer`)
        return this.$refs.lpoModal.openModal()
      }
      this.$store.commit(`setLPO`, this.lpoDetails);
      this.$store.commit(`setCustomer`, this.lpoDetails.debtorID);
      return responses.showSuccess(`LPO set successfully`);
    },
    removeLPO() {
      return Swal.fire({
        icon: `question`,
        title: `Do you want also want to remove customer from sale `,
        showCancelButton: true,
        confirmButtonText: `Sure`,
        cancelButtonText: `No , only remove LPO from sale`,
      }).then(res => {
        if (res.isConfirmed) {
          this.$store.commit(`setCustomer`, ``)
          this.lpoDetails.debtorID = ``
        }
        this.$store.commit(`setLPO`, {
          debtorID: this.lpoDetails.debtorID,
          lpoNumber: undefined,
        });
        this.lpoDetails.lpoNumber = ``
        return responses.showInfo(`LPO removed successfully`)
      })
    },
    openLPO() {
      this.$refs.lpoModal.openModal();
      let obj = this.lpoDetails;
      this.lpoDetails = {
        debtorID: obj.debtorID ? obj.debtorID : this.$store.getters.getCustomer,
        lpoNumber: obj.lpoNumber ? obj.lpoNumber : this.$store.getters.getLPO,
      }
    },
    searchDebtors(input, loading) {
      loading(true);
      return this.$store.dispatch(`getAllDebtors`, {
        Name: input,
        tpin: "",
        contact: ""
      }).finally(() => loading(false))
    },
    setAmount() {
      if (!this.paidAmount) {
        this.paidAmount = Number(this.saleTotal - this.discount.total).toFixed(2)
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.payment-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}

.navigation-bar {
  width: 100%;
  background: var(--card-bg);
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 25px;
  padding-left: 25px;
  border-bottom: 1px solid var(--card-border);
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  box-shadow: var(--card-shadow-sm);

  @media (max-width: 768px) {
    height: 60px;
    padding-left: 15px;
    gap: 15px;
  }

  .back-button {
    font-size: 28px;
    color: var(--text-secondary);
    cursor: pointer;
    transition: var(--transition-speed);

    &:hover {
      color: var(--accent-electric-blue);
    }

    @media (max-width: 768px) {
      font-size: 22px;
    }
  }

  .balance-label {
    color: var(--text-secondary);
    font-size: 0.95rem;
    @media (max-width: 768px) {
      font-size: 0.85rem;
    }
  }

  .balance-amount {
    font-family: var(--font-family);
    font-weight: 700;
    color: var(--text-primary);
    font-size: 1.6rem;
    margin: 0;

    @media (max-width: 768px) {
      font-size: 1.3rem;
    }
  }
}

.payment-body {
  background: var(--bg-gradient);
  min-height: calc(100vh - 70px);
  width: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 15px;
  font-family: var(--font-family);
  color: var(--text-primary);

  @media (max-width: 768px) {
    min-height: calc(100vh - 60px);
    padding: 10px;
    padding-bottom: 80px;
  }
}

.main-payment-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
  background: var(--card-bg);
  border-radius: 15px;
  box-shadow: var(--card-shadow);
  padding: 20px;
  height: fit-content;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  @media (max-width: 768px) {
    padding: 15px;
  }
}

.amount-keypad-section {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.amount-input-container {
  .amount-input-label {
    display: block;
    margin-bottom: 10px;
    font-weight: 600;
    color: var(--text-secondary);
    font-size: 1rem;

    b {
      display: block;
      text-align: center;
      margin-bottom: 8px;
      color: var(--text-primary);
      font-size: 1.1rem;
    }
  }

  .input-amount {
    border: none;
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--text-primary);
    text-align: center;
    width: 100%;
    padding-bottom: 8px;
    border-bottom: 2px solid var(--card-border);
    background-color: transparent;
    transition: var(--transition-speed);

    &:focus, &:active {
      border-bottom: 3px solid var(--accent-electric-blue) !important;
      outline: none;
    }

    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }
}

.keypad-container {
  width: 70%;
  margin-left: 15%;
  margin-bottom: 20px;
  align-items: center;
  justify-content: center;

  .keypad-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    width: 100%;

    .number-btn {
      background: var(--bg-secondary);
      border-radius: 10px;
      border: 1px solid var(--card-border);
      height: 55px;
      font-size: 1.5rem;
      font-weight: 600;
      color: var(--text-primary);
      transition: var(--transition-speed);
      box-shadow: var(--card-shadow-sm);
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        background: var(--accent-electric-blue);
        color: white;
        border-color: var(--accent-electric-blue);
        box-shadow: var(--card-shadow-hover);
        transform: translateY(-1px);
      }

      &:active {
        transform: translateY(0);
        background: var(--accent-electric-blue-dark);
      }

      &.function-btn {
        background: var(--accent-electric-blue);
        color: white;
        font-size: 1.3rem;

        &:hover {
          background: var(--accent-electric-blue-dark);
        }
      }

      svg {
        font-size: 1.2rem;
      }

      @media (max-width: 768px) {
        height: 50px;
        font-size: 1.3rem;
      }
    }
  }

  @media (max-width: 768px) {
    width: 85%;
    margin-left: 7.5%;
    margin-bottom: 15px;
  }
}

.payment-methods-section {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.tip-badge-container {
  display: flex;
  justify-content: flex-end;

  .badge {
    background-color: var(--info-color) !important;
    color: white;
    font-size: 0.95rem;
    padding: 6px 10px;
    border-radius: 8px;
  }
}

.bottom-section {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.order-discount-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
}

.lpo-section, .discount-section {
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: var(--card-shadow);
  padding: 15px;

  h6 {
    font-size: 1rem;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 10px 0;
  }

  @media (max-width: 768px) {
    padding: 12px;
  }
}

.lpo-content {
  .lpo-btn {
    background: var(--secondary-button-bg);
    color: var(--text-primary);
    border: 1px solid var(--card-border);
    border-radius: 8px;
    padding: 8px 15px;
    font-size: 0.85rem;
    font-weight: 600;
    transition: var(--transition-speed);
    box-shadow: var(--card-shadow-sm);
    width: 100%;
    text-align: center;

    &:hover {
      background: var(--secondary-button-hover-bg);
      box-shadow: var(--card-shadow-hover);
    }

    &.lpo-remove-btn {
      background: var(--danger);
      color: white;
      border-color: var(--danger);

      &:hover {
        background: var(--danger-dark);
        border-color: var(--danger-dark);
      }
    }

    &.disabled {
      opacity: 0.6;
      cursor: not-allowed;
      pointer-events: none;
      background: var(--disabled-button-bg) !important;
      color: var(--disabled-button-text) !important;
      border-color: var(--disabled-button-bg) !important;

      &:hover {
        background: var(--disabled-button-bg) !important;
        box-shadow: var(--card-shadow-sm);
      }
    }
  }
}

.discount-box {
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 10px 12px;
  background-color: var(--card-bg);
  color: var(--text-primary);
  cursor: pointer;
  transition: var(--transition-speed);
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 40px;

  &:hover {
    border-color: var(--accent-electric-blue);
  }

  .discount-content {
    p {
      margin: 0;
      font-size: 0.9rem;
      font-weight: 500;
      color: var(--text-primary);
    }
  }

  .discount-actions {
    display: flex;
    gap: 8px;

    .icon-btn {
      color: var(--text-secondary);
      font-size: 1rem;
      cursor: pointer;
      transition: var(--transition-speed);

      &:hover {
        color: var(--accent-electric-blue);
      }

      &.text-danger {
        color: var(--danger) !important;

        &:hover {
          color: var(--danger-dark) !important;
        }
      }
    }
  }
}

.reference-section {
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: var(--card-shadow);
  padding: 15px;

  @media (max-width: 768px) {
    padding: 12px;
  }

  label {
    margin-bottom: 8px;
    font-weight: 600;
    color: var(--text-secondary);
    font-size: 0.95rem;
  }

  textarea {
    font-size: 0.9rem;
    padding: 10px 12px;
    border: 1px solid var(--card-border);
    border-radius: 8px;
    background-color: var(--card-bg);
    color: var(--text-primary);
    transition: var(--transition-speed);
    resize: vertical;

    &:focus {
      border-color: var(--accent-electric-blue);
      box-shadow: 0 0 0 2px rgba(var(--accent-electric-blue-rgb), 0.15);
      outline: none;
    }

    &::placeholder {
      color: var(--text-secondary);
      opacity: 0.7;
    }
  }
}

.mobile-process-button-container {
  padding: 15px;
}

.payment-btn {
  width: 90%;
  height: 55px;
  position: fixed;
  bottom: 15px;
  left: 5%;
  right: 5%;
  z-index: 100;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 700;
  box-shadow: var(--card-shadow-lg);

  span.spinner-border {
    margin-right: 8px;
  }
}

@media (max-width: 576px) {
  .main-payment-section {
    padding: 12px;
  }

  .order-discount-row {
    gap: 8px;
  }

  .lpo-section, .discount-section, .reference-section {
    padding: 10px;
  }
}
</style>
